import type React from "react";
import type { ComponentProps, ReactNode } from "react";
import type {
	ColorAreaProps as AriaColorAreaProps,
	ColorSliderProps as AriaColorSliderProps,
	ColorSwatchPickerItemProps as AriaColorSwatchPickerItemProps,
	ColorThumbProps as AriaColorThumbProps,
	Color,
	ColorSpace,
} from "react-aria-components";

import { Dropper, Plus } from "@untitledui/icons";
import { useRef, useState } from "react";
import {
	ColorArea as AriaColorArea,
	ColorField as AriaColorField,
	ColorSlider as AriaColorSlider,
	ColorSwatch as AriaColorSwatch,
	ColorSwatchPicker as AriaColorSwatchPicker,
	ColorSwatchPickerItem as AriaColorSwatchPickerItem,
	ColorThumb as AriaColorThumb,
	Input as AriaInput,
	SliderTrack as AriaSliderTrack,
	parseColor,
} from "react-aria-components";

import { Button, type ButtonProps } from "@/components/ui/buttons/button";
import { Select } from "@/components/ui/select/select";
import { cx } from "@/utils/cx";

import type { ColorDisplayFormat } from "./color-picker-utils";

import { ColorPickerProvider } from "./color-picker-provider";
import { selectAllOnFocus } from "./color-picker-utils";
import { useColorPicker } from "./use-color-picker";

const COLOR_FORMAT_ITEMS = [
	{ id: "hex", label: "Hex" },
	{ id: "rgb", label: "RGB" },
	{ id: "css", label: "CSS" },
	{ id: "hsl", label: "HSL" },
	{ id: "hsb", label: "HSB" },
];

const ColorThumb = ({ className, ...props }: AriaColorThumbProps) => (
	<AriaColorThumb
		className={(state) =>
			cx(
				"size-5 cursor-grab rounded-full border-[3px] border-fg-white shadow-md outline-0 outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
				state.isDragging && "cursor-grabbing",
				typeof className === "function" ? className(state) : className,
			)
		}
		{...props}
	/>
);

const ColorPickerArea = ({
	className,
	style,
	...props
}: AriaColorAreaProps) => (
	<AriaColorArea
		aria-label="Color picker"
		colorSpace="hsb"
		xChannel="saturation"
		yChannel="brightness"
		{...props}
		className={(state) =>
			cx(
				"relative aspect-square w-full shrink-0 rounded-lg ring-[0.5px] ring-alpha-black/10 ring-inset",
				typeof className === "function" ? className(state) : className,
			)
		}
		style={({ defaultStyle }) => ({
			...defaultStyle,
			borderRadius: 8,
			...style,
		})}
	>
		<ColorThumb />
	</AriaColorArea>
);

const HueSlider = ({
	className,
	...props
}: Omit<AriaColorSliderProps, "channel">) => (
	<AriaColorSlider
		aria-label="Hue"
		{...props}
		channel="hue"
		className={(state) =>
			cx(
				"w-full",
				typeof className === "function" ? className(state) : className,
			)
		}
	>
		<AriaSliderTrack
			className="relative h-3 w-full cursor-pointer rounded-full ring-[0.5px] ring-alpha-black/10 ring-inset"
			style={({ defaultStyle }) => ({ ...defaultStyle, borderRadius: 9999 })}
		>
			<ColorThumb
				style={({ defaultStyle }) => ({ ...defaultStyle, top: "50%" })}
			/>
		</AriaSliderTrack>
	</AriaColorSlider>
);

const AlphaSlider = ({
	className,
	...props
}: Omit<AriaColorSliderProps, "channel">) => (
	<AriaColorSlider
		aria-label="Alpha"
		{...props}
		channel="alpha"
		className={(state) =>
			cx(
				"w-full",
				typeof className === "function" ? className(state) : className,
			)
		}
	>
		<AriaSliderTrack
			className="relative h-3 w-full cursor-pointer rounded-full ring-[0.5px] ring-alpha-black/10 ring-inset"
			style={({ defaultStyle }) => ({
				...defaultStyle,
				background: `${defaultStyle.background}, repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 50% / 8px 8px`,
				borderRadius: 9999,
			})}
		>
			<ColorThumb
				style={({ defaultStyle }) => ({ ...defaultStyle, top: "50%" })}
			/>
		</AriaSliderTrack>
	</AriaColorSlider>
);

const EyeDropperButton = (props: ButtonProps) => {
	const { actions } = useColorPicker();

	const isSupported = typeof window !== "undefined" && "EyeDropper" in window;
	if (!isSupported) return null;

	return (
		<Button
			color="secondary"
			iconLeading={Dropper}
			onClick={async () => {
				try {
					// @ts-expect-error EyeDropper API not typed
					const result = await new EyeDropper().open();
					actions.setColor(parseColor(result.sRGBHex));
				} catch {
					/* cancelled */
				}
			}}
			size="sm"
			{...props}
		/>
	);
};

const ColorFormatSelect = ({
	className,
	...props
}: Omit<ComponentProps<typeof Select>, "children">) => {
	const { actions, state } = useColorPicker();

	return (
		<Select
			aria-label="Color format"
			className={(state) =>
				cx(
					"w-20 shrink-0",
					typeof className === "function" ? className(state) : className,
				)
			}
			items={COLOR_FORMAT_ITEMS}
			onChange={(value) => actions.setColorFormat(value as ColorDisplayFormat)}
			size="sm"
			value={state.colorFormat}
			{...props}
		>
			{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
		</Select>
	);
};

/** Wrapper that mirrors InputBase styling with customizable border radius for input groups. */
export const InputCell = ({
	children,
	className,
	position = "only",
}: {
	children: React.ReactNode;
	className?: string;
	position?: "first" | "last" | "middle" | "only";
}) => {
	const radius = {
		first: "rounded-l-lg",
		last: "rounded-r-lg",
		middle: "rounded-none",
		only: "rounded-lg",
	}[position];

	return (
		<div
			className={cx(
				"relative flex place-content-center place-items-center bg-primary ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset focus-within:z-10 focus-within:ring-2 focus-within:ring-brand",
				radius,
				position !== "only" && position !== "first" && "-ml-px",
				className,
			)}
		>
			{children}
		</div>
	);
};

interface SwatchItemsProps extends AriaColorSwatchPickerItemProps {
	size?: "md" | "sm";
}

export const SwatchItem = ({
	color,
	size = "md",
	...props
}: SwatchItemsProps) => (
	<AriaColorSwatchPickerItem
		{...props}
		className={(state) =>
			cx(
				"cursor-pointer rounded-full",
				size === "sm" ? "size-4" : "size-5",
				state.isFocusVisible
					? "outline-2 outline-offset-2 outline-focus-ring"
					: state.isSelected
						? "outline-2 outline-offset-2 outline-(--swatch-color)"
						: "outline-0",
				typeof props.className === "function"
					? props.className(state)
					: props.className,
			)
		}
		color={color}
		style={{ "--swatch-color": color } as React.CSSProperties}
	>
		<AriaColorSwatch
			className="size-full rounded-full ring-1 ring-alpha-black/10 ring-inset"
			style={({ defaultStyle }) => defaultStyle}
		/>
	</AriaColorSwatchPickerItem>
);

const channelInputClass =
	"w-full min-w-0 bg-transparent px-2.5 py-2 text-sm text-primary outline-hidden";

const CHANNEL_CONFIG = {
	hsb: {
		channels: ["hue", "saturation", "brightness"] as const,
		colorSpace: "hsb" as ColorSpace,
	},
	hsl: {
		channels: ["hue", "saturation", "lightness"] as const,
		colorSpace: "hsl" as ColorSpace,
	},
	rgb: {
		channels: ["red", "green", "blue"] as const,
		colorSpace: "rgb" as ColorSpace,
	},
};

const ColorValueInput = () => {
	const { state } = useColorPicker();
	if (state.colorFormat === "hex") return <HexInput />;
	if (state.colorFormat === "css") return <CssInput />;
	return <ChannelInput format={state.colorFormat} />;
};

const HexInput = () => {
	const { actions, state } = useColorPicker();
	return (
		<div className="flex flex-1 shadow-xs">
			<InputCell className="flex-1" position="first">
				<AriaColorField
					aria-label="Hex color"
					className="flex flex-1 items-center gap-2 px-2.5 py-2"
					onChange={(c) => {
						if (c) actions.setColor(c);
					}}
					value={state.color}
				>
					<AriaColorSwatch
						className="size-4 shrink-0 rounded-full ring-1 ring-alpha-black/10 ring-inset"
						style={({ defaultStyle }) => defaultStyle}
					/>
					<AriaInput
						className="w-full min-w-0 bg-transparent text-sm text-primary outline-hidden"
						onFocus={selectAllOnFocus}
					/>
				</AriaColorField>
			</InputCell>
			<InputCell className="w-14 shrink-0" position="last">
				<AriaColorField
					aria-label="Alpha"
					channel="alpha"
					className="flex w-full items-center"
					onChange={(c) => {
						if (c) actions.setColor(c);
					}}
					value={state.color}
				>
					<AriaInput
						className="w-full min-w-0 bg-transparent px-2.5 py-2 text-sm text-primary outline-hidden"
						onFocus={selectAllOnFocus}
					/>
				</AriaColorField>
			</InputCell>
		</div>
	);
};

const CssInput = () => {
	const { actions, state } = useColorPicker();
	const [draft, setDraft] = useState<null | string>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const toRgba = (c: Color) => {
		const rgb = c.toFormat("rgb");
		return `rgba(${Math.round(rgb.getChannelValue("red"))}, ${Math.round(rgb.getChannelValue("green"))}, ${Math.round(rgb.getChannelValue("blue"))}, ${Math.round(rgb.getChannelValue("alpha") * 100) / 100})`;
	};

	return (
		<InputCell className="flex-1 shadow-xs">
			<input
				className="w-full min-w-0 bg-transparent px-3 py-2 text-sm text-primary outline-hidden"
				onBlur={() => {
					if (draft !== null) {
						try {
							actions.setColor(parseColor(draft));
						} catch {
							/* */
						}
						setDraft(null);
					}
				}}
				onChange={(e) => setDraft(e.target.value)}
				onFocus={(e) => {
					const val = toRgba(state.color);
					setDraft(val);
					requestAnimationFrame(() => {
						const s = val.indexOf("(") + 1;
						const end = val.indexOf(")");
						if (s > 0 && end > s) e.target.setSelectionRange(s, end);
					});
				}}
				ref={inputRef}
				value={draft ?? toRgba(state.color)}
			/>
		</InputCell>
	);
};

const ChannelInput = ({ format }: { format: ColorDisplayFormat }) => {
	const { actions, state } = useColorPicker();
	const config = CHANNEL_CONFIG[format as keyof typeof CHANNEL_CONFIG];
	if (!config) return null;
	const converted = state.color.toFormat(config.colorSpace);

	return (
		<div className="flex flex-1 shadow-xs">
			{config.channels.map((ch, i) => (
				<InputCell
					className="flex-1"
					key={ch}
					position={i === 0 ? "first" : "middle"}
				>
					<AriaColorField
						aria-label={ch.charAt(0).toUpperCase() + ch.slice(1)}
						channel={ch}
						className="flex w-full"
						colorSpace={config.colorSpace}
						onChange={(c) => {
							if (c) actions.setColor(c);
						}}
						value={converted}
					>
						<AriaInput
							className={channelInputClass}
							onFocus={selectAllOnFocus}
						/>
					</AriaColorField>
				</InputCell>
			))}
			<InputCell className="w-14 shrink-0" position="last">
				<AriaColorField
					aria-label="Alpha"
					channel="alpha"
					className="flex w-full"
					onChange={(c) => {
						if (c) actions.setColor(c);
					}}
					value={converted}
				>
					<AriaInput className={channelInputClass} onFocus={selectAllOnFocus} />
				</AriaColorField>
			</InputCell>
		</div>
	);
};

export interface SavedColorsProps {
	/** Array of CSS color strings or gradient strings to display as swatches. */
	colors: string[];
	/** Whether to show the "Add" button. */
	isCustomColorsEnabled?: boolean;
	/** Heading text above the swatch list. */
	label?: string;
	/** Called when the "Add" button is clicked. Omit to hide the button. */
	onAdd?: () => void;
	/** Called when a swatch is selected, with the color/gradient string. */
	onSelect?: (color: string) => void;
}

const SavedColors = ({
	colors,
	isCustomColorsEnabled = false,
	label = "Saved",
	onAdd,
	onSelect,
}: SavedColorsProps) => {
	// Check if colors are solid (parseable) or gradients
	const firstColor = colors[0];
	const isSolid =
		firstColor !== undefined &&
		(() => {
			try {
				parseColor(firstColor);
				return true;
			} catch {
				return false;
			}
		})();

	return (
		<div className="flex flex-col gap-3">
			{isCustomColorsEnabled && (
				<div className="flex items-center gap-3">
					<p className="flex-1 text-sm font-semibold text-secondary">{label}</p>
					{onAdd && (
						<Button
							color="link-gray"
							iconLeading={Plus}
							onClick={onAdd}
							size="sm"
						>
							Add
						</Button>
					)}
				</div>
			)}
			{isSolid ? (
				<AriaColorSwatchPicker
					aria-label={label}
					className="flex flex-wrap gap-2"
					onChange={(color) => onSelect?.(color.toString("hex"))}
				>
					{colors.map((c) => (
						<SwatchItem color={c} key={c} />
					))}
				</AriaColorSwatchPicker>
			) : (
				<div className="flex flex-wrap gap-2">
					{colors.map((c, i) => (
						<button
							aria-label="Select gradient"
							className="size-5 cursor-pointer rounded-full ring-1 ring-alpha-black/10 transition duration-100 ease-linear ring-inset hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
							key={`${c}-${i}`}
							onClick={() => onSelect?.(c)}
							style={{ background: c }}
							type="button"
						/>
					))}
				</div>
			)}
		</div>
	);
};

/** All-in-one solid color picker layout */
const ColorPickerSolid = () => (
	<div className="flex flex-col gap-4">
		<ColorPickerArea />
		<div className="flex flex-col gap-4">
			<div className="flex items-start gap-3">
				<EyeDropperButton />
				<div className="flex flex-1 flex-col gap-3">
					<HueSlider />
					<AlphaSlider />
				</div>
			</div>
			<div className="flex items-center gap-3">
				<ColorFormatSelect />
				<ColorValueInput />
			</div>
		</div>
	</div>
);

/** Card container for the color picker. */
const ColorPickerDialog = ({
	children,
	className,
}: {
	children?: ReactNode;
	className?: string;
}) => (
	<div
		className={cx(
			"relative flex w-80 flex-col overflow-clip rounded-2xl bg-primary shadow-xl ring-1 ring-secondary_alt",
			className,
		)}
	>
		{children}
	</div>
);

/** Compound export */
const ColorPicker = Object.assign(ColorPickerSolid, {
	AlphaSlider,
	Area: ColorPickerArea,
	ColorFormatSelect,
	ColorValueInput,
	Dialog: ColorPickerDialog,
	EyeDropper: EyeDropperButton,
	HueSlider,
	Provider: ColorPickerProvider,
	SavedColors,
	SwatchItem,
});

export { ColorPicker };
